import { restClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'

const q = (p: Record<string, unknown>) => new URLSearchParams(
  Object.entries(p).filter(([, v]) => v !== undefined && v !== null && v !== '').reduce((a, [k, v]) => ({ ...a, [k]: String(v) }), {})
).toString()

export const systemService = {
  cities: {
    list: (p: { page?: number; limit?: number; search?: string; is_published?: 0 | 1; locale?: string } = {}) =>
      restClient.get(`${endpoints.city.list}?${q({ page: p.page??1, limit: p.limit??100, search: p.search, is_published: p.is_published, locale: p.locale ?? 'en' })}`).then(r => r.data),
    // Real shape (per old app): a city's own translation + its pricing/certificate settings,
    // all in one call — not a flat {name, name_ar}. Create and update are the SAME
    // endpoint (admin/city/create-city-translation) — omitting city_id creates a
    // brand-new city, sending it updates the existing one.
    createUpdate: (d: {
      city_id?: number; locale: string; name: string
      is_published?: boolean; is_certificate_available?: boolean
      show_to_trainer?: boolean; show_to_student?: boolean; show_to_school?: boolean
      certificate_price?: number; certificate_link?: string
      reschedule_fee?: number; trainer_change_fee?: number; pickup_fee?: number; max_slot?: number
      price_per_km?: number; min_price?: number
      free_reschedule_before?: number; free_reschedule_before_type?: 0 | 1
    }) => {
      const payload: Record<string, unknown> = {
        ...(d.city_id ? { city_id: d.city_id } : {}),
        is_published: d.is_published ? 1 : 0,
        is_certificate_available: d.is_certificate_available ? 1 : 0,
        show_to_trainer: d.show_to_trainer ? 1 : 0,
        show_to_student: d.show_to_student ? 1 : 0,
        show_to_school: d.show_to_school ? 1 : 0,
        city_translation: [{ locale: d.locale, name: d.name }],
      }
      if (d.is_certificate_available) {
        payload.certificate_price = Number(d.certificate_price ?? 0)
        payload.certificate_link = d.certificate_link ?? ''
      }
      if (d.reschedule_fee)       payload.reschedule_fee = Number(d.reschedule_fee)
      if (d.trainer_change_fee)   payload.trainer_change_fee = Number(d.trainer_change_fee)
      if (d.pickup_fee)           payload.pickup_fee = Number(d.pickup_fee)
      if (d.max_slot)             payload.max_slot = Number(d.max_slot)
      if (d.price_per_km)        payload.price_per_km = Number(d.price_per_km)
      if (d.min_price)            payload.min_price = Number(d.min_price)
      if (d.free_reschedule_before) payload.free_reschedule_before = Number(d.free_reschedule_before)
      if (d.free_reschedule_before_type != null) payload.free_reschedule_before_type = Number(d.free_reschedule_before_type)
      return restClient.post(endpoints.city.create, payload).then(r => r.data)
    },
    delete: (id: number) => restClient.delete(`${endpoints.city.delete}${id}`).then(r => r.data),
  },

  states: {
    list: (p: { page?: number; limit?: number; search?: string; is_published?: 0 | 1; city_id?: number; locale?: string } = {}) =>
      restClient.get(`${endpoints.state.getList}?${q({ page: p.page??1, limit: p.limit??100, search: p.search, is_published: p.is_published, city_id: p.city_id, locale: p.locale ?? 'en' })}`).then(r => r.data),
    // Real shape: FormData, scoped to a city, with a single translation + an order number.
    createUpdate: (d: { state_id?: number; city_id: number; locale: string; name: string; order: number; is_published?: boolean }) => {
      const fd = new FormData()
      fd.append('city_id', String(d.city_id))
      fd.append('is_published', d.is_published ? '1' : '0')
      fd.append('translations[0][locale]', d.locale)
      fd.append('translations[0][name]', d.name)
      fd.append('order', String(d.order))
      if (d.state_id) fd.append('state_id', String(d.state_id))
      return restClient.post(endpoints.state.create, fd).then(r => r.data)
    },
    delete: (id: number) => restClient.delete(`${endpoints.state.deleteById}/${id}`).then(r => r.data),
  },

  dialects: {
    list: (p: { page?: number; limit?: number; search?: string; is_published?: 0 | 1 } = {}) =>
      restClient.get(`${endpoints.dialect.list}?${q({ page: p.page??1, limit: p.limit??100, search: p.search, is_published: p.is_published })}`).then(r => r.data),
    // Real shape: FormData, a language/dialect entry — has nothing to do with name/name_ar.
    createUpdate: (d: { dialect_id?: number; language_name: string; dialect_name?: string; keywords?: string; description?: string; order?: number; is_published?: boolean }) => {
      const fd = new FormData()
      fd.append('is_published', d.is_published ? '1' : '0')
      fd.append('language_name', d.language_name)
      fd.append('dialect_name', d.dialect_name ?? '')
      fd.append('keywords', d.keywords ?? '')
      fd.append('description', d.description ?? '')
      fd.append('order', String(d.order ?? ''))
      if (d.dialect_id) fd.append('dialect_id', String(d.dialect_id))
      return restClient.post(endpoints.dialect.createUpdate, fd).then(r => r.data)
    },
    delete: (id: number) => restClient.delete(`${endpoints.dialect.delete}/${id}`).then(r => r.data),
  },

  languages: {
    list: (p: { page?: number; limit?: number } = {}) =>
      restClient.get(`${endpoints.language.list}?${q({ page: p.page??1, limit: p.limit??100 })}`).then(r => r.data),
    // Uploads the flag image first, then create/update needs the returned image id as flag_id.
    uploadFlag: (file: File) => {
      const fd = new FormData()
      fd.append('picture', file)
      return restClient.post(endpoints.allimage.createSingle, fd).then(r => r.data as { data: { id: number }; message: string })
    },
    create: (d: { flag_id: number; language_culture: string; name: string; display_order: number; published: boolean }) =>
      restClient.post(endpoints.language.create, d).then(r => r.data),
    update: (id: number, d: { flag_id: number; language_culture: string; name: string; display_order: number; published: boolean }) =>
      restClient.put(`${endpoints.language.update}${id}`, d).then(r => r.data),
    delete: (id: number) => restClient.delete(`${endpoints.language.delete}${id}`).then(r => r.data),
  },

  pickups: {
    list: (p: { page?: number; limit?: number; search?: string } = {}) =>
      restClient.get(`${endpoints.pickup.list}?${q({ page: p.page??1, limit: p.limit??100, search: p.search })}`).then(r => r.data),
    // Real shape: FormData — a city-scoped date/time exclusion window, not name/name_ar.
    createUpdate: (d: { id?: number; city_id: number; start_date: string; end_date: string; start_time: string; end_time: string; status: boolean }) => {
      const fd = new FormData()
      if (d.id) fd.append('id', String(d.id))
      fd.append('city_id', String(d.city_id))
      fd.append('start_date', d.start_date)
      fd.append('end_date', d.end_date)
      fd.append('start_time', d.start_time)
      fd.append('end_time', d.end_time)
      fd.append('status', d.status ? '1' : '0')
      return restClient.post(endpoints.pickup.createUpdate, fd).then(r => r.data)
    },
    delete: (id: number) => restClient.delete(`${endpoints.pickup.delete}?id=${id}`).then(r => r.data),
  },

  appSettings: {
    // Old app always sends locale + sort params; without them the backend may silently
    // paginate/sort differently (or the new UI's unfiltered fetch truncates results).
    get: (p: { page?: number; limit?: number; locale?: string } = {}) =>
      restClient.get(`${endpoints.appSettings.list}?${q({
        page: p.page ?? 1, limit: p.limit ?? 1000, sort_by: 'display_order', sort_order: 'asc', locale: p.locale ?? 'en',
      })}`).then(r => r.data),
    update: (d: { id: number; key: string; value: string }) => restClient.post(endpoints.appSettings.update, d).then(r => r.data),
  },

  termsConditions: {
    // Real shape: requires a locale, and returns rows whose `value` is an array of
    // {heading, content} sections — not a single flat string.
    get: (locale: string) => restClient.get(`${endpoints.termsAndConditions.getList}?locale=${locale}`).then(r => r.data),
    update: (d: { content: Array<{ heading: string; content: string }>; locale: string; display_order?: number }) =>
      restClient.post(endpoints.termsAndConditions.createUpdate, d).then(r => r.data),
  },

  // Dynamic Services SETTINGS — defines the service TYPES offered (fees, city,
  // icon, active/order). Distinct from the registrations-approval flow in
  // ServicesPage.tsx, which lists user submissions against these services.
  dynamicServices: {
    list: (p: { page?: number; limit?: number; city_id?: string } = {}) =>
      restClient.get(`${endpoints.dynamicServiceSettings.list}?${q({ page: p.page ?? 1, limit: p.limit ?? 10, city_id: p.city_id })}`).then(r => r.data),
    createUpdate: (d: {
      id?: number; name: string; name_ar: string; subtitle: string; subtitle_ar: string
      fees: number; currency: string; city_id?: string; display_order?: number; is_active: boolean
      icon?: File | null
    }) => {
      const fd = new FormData()
      fd.append('name', d.name)
      fd.append('name_ar', d.name_ar)
      fd.append('subtitle', d.subtitle)
      fd.append('subtitle_ar', d.subtitle_ar)
      fd.append('fees', String(d.fees))
      fd.append('currency', d.currency)
      if (d.city_id) fd.append('city_id', d.city_id)
      fd.append('is_active', d.is_active ? '1' : '0')
      if (d.display_order !== undefined && d.display_order !== null) fd.append('display_order', String(d.display_order))
      if (d.icon) fd.append('icon', d.icon)
      return d.id
        ? restClient.post(`${endpoints.dynamicServiceSettings.update}${d.id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data)
        : restClient.post(endpoints.dynamicServiceSettings.create, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data)
    },
    delete: (id: number) => restClient.delete(`${endpoints.dynamicServiceSettings.delete}${id}`).then(r => r.data),
  },
}
